import { 
  InvoiceColumnConfiguration, 
  CreateColumnConfigRequest, 
  UpdateColumnConfigRequest,
  ApiResponse,
  PaginatedResponse,
  PaginationParams
} from '../../types/api'
import { apiClient } from '../../utils/api'

export class ColumnConfigService {
  private readonly basePath = '/column-configurations'

  // Get all column configurations
  async getColumnConfigurations(
    pagination?: PaginationParams
  ): Promise<ApiResponse<PaginatedResponse<InvoiceColumnConfiguration>>> {
    const params = pagination || {}
    return apiClient.get(`${this.basePath}`, params)
  }

  // Get specific column configuration
  async getColumnConfiguration(id: string): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.get(`${this.basePath}/${id}`)
  }

  // Get active column configuration for current user/branch
  async getActiveColumnConfiguration(): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.get(`${this.basePath}/active`)
  }

  // Get default column configuration
  async getDefaultColumnConfiguration(): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.get(`${this.basePath}/default`)
  }

  // Create new column configuration
  async createColumnConfiguration(data: CreateColumnConfigRequest): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.post(`${this.basePath}`, data)
  }

  // Update column configuration
  async updateColumnConfiguration(id: string, data: UpdateColumnConfigRequest): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.put(`${this.basePath}/${id}`, data)
  }

  // Delete column configuration
  async deleteColumnConfiguration(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}`)
  }

  // Set as active configuration for current user
  async setActiveConfiguration(id: string): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.post(`${this.basePath}/${id}/activate`)
  }

  // Duplicate configuration
  async duplicateConfiguration(id: string, name: string): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.post(`${this.basePath}/${id}/duplicate`, { name })
  }

  // Reset to default configuration
  async resetToDefault(): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.post(`${this.basePath}/reset-to-default`)
  }

  // Export configuration
  async exportConfiguration(id: string): Promise<ApiResponse<{ url: string }>> {
    return apiClient.get(`${this.basePath}/${id}/export`)
  }

  // Import configuration
  async importConfiguration(file: File): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    const formData = new FormData()
    formData.append('file', file)
    
    return apiClient.post(`${this.basePath}/import`, formData)
  }

  // Get configuration templates
  async getConfigurationTemplates(): Promise<ApiResponse<InvoiceColumnConfiguration[]>> {
    return apiClient.get(`${this.basePath}/templates`)
  }

  // Create configuration from template
  async createFromTemplate(templateId: string, name: string): Promise<ApiResponse<InvoiceColumnConfiguration>> {
    return apiClient.post(`${this.basePath}/templates/${templateId}/create`, { name })
  }
}

// Create and export service instance
export const columnConfigService = new ColumnConfigService() 